import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { MdAdd, MdDeleteOutline, MdEdit, MdOutlineFileDownload } from "react-icons/md";
import Btn from "../shared/button/Btn";
import SmallSpinner from "../shared/spinners/SmallSpinner";
import {
  deleteDriverSavedAddress,
  getDriverSavedAddresses,
  resetDriverAddresses,
} from "../../features/driverAddress/driverAddressSlice";
import DriverAddressFormModal from "./DriverAddressFormModal";
import DriverAddressImportModal from "./DriverAddressImportModal";

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .toolbar, .add-options, .group-title, .card-actions, .badges, .confirm-actions {
    display: flex;
    align-items: center;
  }

  .toolbar { position: relative; justify-content: space-between; gap: 12px; }
  .toolbar-title h4 { margin: 0 0 4px; }
  .toolbar-title p { margin: 0; font-size: 12px; opacity: 0.75; }

  .add-options {
    position: absolute;
    top: 46px;
    right: 0;
    z-index: 4;
    width: 260px;
    align-items: stretch;
    flex-direction: column;
    padding: 8px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 8px;
    background: ${({ theme }) => theme.mainBgColor};
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.18);
  }

  .add-option {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 11px;
    border-radius: 6px;
    text-align: left;
    color: ${({ theme }) => theme.mainTextColor};
  }

  .add-option:hover { background: ${({ theme }) => theme.secondaryBgColor}; }

  .content {
    width: 100%;
    max-height: 510px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding-right: 5px;
  }

  .group { display: flex; flex-direction: column; gap: 9px; }
  .group-title { justify-content: space-between; padding-bottom: 7px; border-bottom: 1px solid ${({ theme }) => theme.mainBorderColor}; }
  .group-title h5 { margin: 0; font-size: 14px; }
  .count { font-size: 12px; opacity: 0.7; }

  .card {
    display: grid;
    grid-template-columns: minmax(150px, 0.8fr) minmax(220px, 1.5fr) minmax(160px, 0.8fr) auto;
    gap: 16px;
    align-items: center;
    padding: 14px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 9px;
    background: ${({ theme }) => theme.mainBgColor};
  }

  .card-title { font-weight: 700; margin-bottom: 6px; }
  .card-text { font-size: 13px; overflow-wrap: anywhere; }
  .secondary { margin-top: 4px; font-size: 12px; opacity: 0.72; }
  .badges { flex-wrap: wrap; gap: 5px; margin-top: 7px; }
  .badge { padding: 3px 7px; border-radius: 999px; color: #fff; background: #6c6c6c; font-size: 11px; }
  .badge.imported { background: #4f873b; }
  .badge.readonly { background: #997239; }

  .card-actions { justify-content: flex-end; gap: 7px; }
  .icon-button { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border: 1px solid ${({ theme }) => theme.mainBorderColor}; border-radius: 7px; color: ${({ theme }) => theme.mainTextColor}; font-size: 18px; }
  .icon-button.delete { color: var(--color-main); }
  .empty, .status { padding: 40px; text-align: center; }

  .confirm-backdrop {
    position: fixed;
    inset: 0;
    z-index: 31;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.68);
  }

  .confirm {
    width: min(430px, calc(100% - 30px));
    padding: 22px;
    border-radius: 10px;
    color: ${({ theme }) => theme.mainTextColor};
    background: ${({ theme }) => theme.mainBgColor};
  }

  .confirm h4 { margin: 0 0 10px; }
  .confirm p { margin: 0; font-size: 13px; line-height: 1.5; }
  .confirm-actions { justify-content: flex-end; gap: 10px; margin-top: 20px; }

  @media (max-width: 950px) {
    .card { grid-template-columns: 1fr 1.5fr; }
    .card-actions { justify-content: flex-start; }
  }

  @media (max-width: 650px) {
    .toolbar { align-items: stretch; flex-direction: column; }
    .add-options { position: static; width: 100%; }
    .card { grid-template-columns: 1fr; }
  }
`;

const AddressCard = ({ address, targetOwnerUserId, onEdit, onDelete }) => {
  const isInherited =
    !address.canManage &&
    address.ownerUserId &&
    targetOwnerUserId &&
    address.ownerUserId !== targetOwnerUserId;

  return (
    <div className="card">
      <div>
        <div className="card-title">{address.title || "İsimsiz Adres"}</div>
        <div className="card-text">{address.tag || "Etiket yok"}</div>
        <div className="badges">
          {address.isImported && <span className="badge imported">Aktarılan adres</span>}
          {!address.canManage && <span className="badge readonly">Salt okunur</span>}
        </div>
      </div>
      <div>
        <div className="card-text">
          {[address.town, address.city].filter(Boolean).join(" / ") || "Konum bilgisi yok"}
        </div>
        <div className="secondary">{address.address || "Açık adres girilmemiş"}</div>
        {isInherited && address.ownerName && (
          <div className="secondary">{address.ownerName} sürücüsünden geliyor</div>
        )}
      </div>
      <div>
        <div className="card-text">{address.contactPersonName || "Yetkili belirtilmemiş"}</div>
        <div className="secondary">{address.contactPersonPhoneNumber || "Telefon belirtilmemiş"}</div>
        <div className="secondary">{address.lat && address.lng ? `${address.lat}, ${address.lng}` : "Koordinat yok"}</div>
      </div>
      <div className="card-actions">
        {address.canManage && (
          <>
            <button type="button" className="icon-button" aria-label="Adresi düzenle" title="Düzenle" onClick={() => onEdit(address)}>
              <MdEdit />
            </button>
            <button type="button" className="icon-button delete" aria-label="Adresi sil" title="Sil" onClick={() => onDelete(address)}>
              <MdDeleteOutline />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const AddressGroup = ({ title, items, targetOwnerUserId, onEdit, onDelete, emptyText }) => (
  <section className="group">
    <div className="group-title">
      <h5>{title}</h5>
      <span className="count">{items.length} adres</span>
    </div>
    {items.length === 0 ? (
      <div className="empty">{emptyText}</div>
    ) : (
      items.map((address) => (
        <AddressCard
          key={address.id}
          address={address}
          targetOwnerUserId={targetOwnerUserId}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))
    )}
  </section>
);

const RegularDriverItemAddresses = ({ targetOwnerUserId }) => {
  const dispatch = useDispatch();
  const { items, isLoading, error, isMutating } = useSelector((state) => state.driverAddress);
  const [addOptionsOpen, setAddOptionsOpen] = useState(false);
  const [formAddress, setFormAddress] = useState(undefined);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    if (targetOwnerUserId) {
      dispatch(
        getDriverSavedAddresses({
          targetOwnerUserId,
          includeInherited: true,
          tag: null,
          searchText: null,
        })
      );
    }
    return () => dispatch(resetDriverAddresses());
  }, [dispatch, targetOwnerUserId]);

  const manageableAddresses = useMemo(
    () => items.filter((address) => address.canManage === true),
    [items]
  );
  const readonlyAddresses = useMemo(
    () => items.filter((address) => address.canManage !== true),
    [items]
  );

  const handleDelete = async () => {
    if (!targetOwnerUserId || !deleteCandidate?.id || deleteCandidate.canManage !== true) return;
    try {
      await dispatch(
        deleteDriverSavedAddress({
          targetOwnerUserId,
          id: deleteCandidate.id,
        })
      ).unwrap();
      setDeleteCandidate(null);
    } catch (_) {
      // Backend mesajı thunk tarafından toast olarak gösterilir.
    }
  };

  if (!targetOwnerUserId) {
    return <Container><div className="status">Sürücünün kullanıcı kimliği bulunamadığı için adresleri yüklenemedi.</div></Container>;
  }

  return (
    <Container>
      <div className="toolbar">
        <div className="toolbar-title">
          <h4>Sürücü Adresleri</h4>
          <p>Sürücünün kendi ve üst sürücülerinden gelen adresleri birlikte gösterilir.</p>
        </div>
        <Btn width="170px" bgColor="#E85446" color="#fff" handleClick={() => setAddOptionsOpen((open) => !open)}>
          <MdAdd /> Adres Ekle
        </Btn>

        {addOptionsOpen && (
          <div className="add-options">
            <button type="button" className="add-option" onClick={() => { setFormAddress(null); setAddOptionsOpen(false); }}>
              <MdAdd /> Yeni Adres Ekle
            </button>
            <button type="button" className="add-option" onClick={() => { setImportOpen(true); setAddOptionsOpen(false); }}>
              <MdOutlineFileDownload /> Mevcut Adres Aktar
            </button>
          </div>
        )}
      </div>

      <div className="content">
        {isLoading && items.length === 0 ? (
          <div className="status"><SmallSpinner size="34px" color="#fff" bgColor="#E85446" /></div>
        ) : error && items.length === 0 ? (
          <div className="status">{error}</div>
        ) : (
          <>
            <AddressGroup
              title="Portal Tarafından Yönetilen Adresler"
              items={manageableAddresses}
              targetOwnerUserId={targetOwnerUserId}
              onEdit={setFormAddress}
              onDelete={setDeleteCandidate}
              emptyText="Portal tarafından eklenen veya aktarılan adres bulunmuyor."
            />
            <AddressGroup
              title="Sürücü ve Üst Sürücü Adresleri"
              items={readonlyAddresses}
              targetOwnerUserId={targetOwnerUserId}
              onEdit={setFormAddress}
              onDelete={setDeleteCandidate}
              emptyText="Sürücünün mobilde eklediği veya miras aldığı adres bulunmuyor."
            />
          </>
        )}
      </div>

      {formAddress !== undefined && (
        <DriverAddressFormModal
          targetOwnerUserId={targetOwnerUserId}
          address={formAddress}
          onClose={() => setFormAddress(undefined)}
        />
      )}

      {importOpen && (
        <DriverAddressImportModal
          targetOwnerUserId={targetOwnerUserId}
          driverAddresses={items}
          onClose={() => setImportOpen(false)}
        />
      )}

      {deleteCandidate && (
        <div className="confirm-backdrop" onClick={() => !isMutating && setDeleteCandidate(null)}>
          <div className="confirm" onClick={(event) => event.stopPropagation()}>
            <h4>Adres geri çekilsin mi?</h4>
            <p>
              “{deleteCandidate.title || "İsimsiz Adres"}” sürücünün adres listesinden ve mobil uygulamasından kaldırılacaktır.
            </p>
            <div className="confirm-actions">
              <Btn width="110px" color="#E85446" bgColor="transparent" handleClick={() => setDeleteCandidate(null)} isDisabled={isMutating}>
                Vazgeç
              </Btn>
              <Btn width="140px" bgColor="#E85446" color="#fff" handleClick={handleDelete} isDisabled={isMutating}>
                {isMutating ? "İşleniyor..." : "Geri Çek"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default RegularDriverItemAddresses;

